import type { ShowcaseAreaKind } from '../../../../services/ecommerceApi';

/**
 * Site Kurulum Sihirbazı - adım tanımları ve taslak veri modeli.
 *
 * Sihirbaz yeni bir sistem kurmaz; her adımın verisi "Kur" anına kadar
 * localStorage'da taslak olarak tutulur, kurulumda mevcut e-ticaret
 * kaydetme API'lerine yazılır (bkz. installEngine.ts).
 */

export type WizardStepId =
  | 'general'
  | 'theme'
  | 'pagesMenu'
  | 'contact'
  | 'products'
  | 'paymentShipping'
  | 'legal';

export const WIZARD_STEP_ORDER: WizardStepId[] = [
  'general',
  'theme',
  'pagesMenu',
  'contact',
  'products',
  'paymentShipping',
  'legal',
];

export type HeaderTemplateId =
  | 'header1'
  | 'header2'
  | 'header3'
  | 'header4'
  | 'header5'
  | 'header6'
  | 'header7';

export interface WizardGeneral {
  siteName: string;
  faviconUrl: string;
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
  metaTitle: string;
  metaDescription: string;
}

export interface WizardTheme {
  headerType: HeaderTemplateId;
  layoutType: string; // 'modern' | 'boxed'
  containerMaxWidth: number;
}

export interface WizardPagesMenu {
  menuEnabled: boolean;
  showHomePage: boolean;
  showAllCategories: boolean;
  faqEnabled: boolean;
  supportEnabled: boolean;
}

export interface WizardContact {
  address: string;
  phone: string;
  email: string;
  workingHours: string;
  mapEnabled: boolean;
}

export interface WizardProducts {
  createShowcase: boolean;
  showcaseTitle: string;
  showcaseKind: ShowcaseAreaKind;
  homepageLimit: number;
}

export interface WizardPaymentShipping {
  shippingEnabled: boolean;
  freeShippingThreshold: number;
  shippingCost: number;
  guestCheckoutEnabled: boolean;
}

export interface WizardLegal {
  seedDefaults: boolean;
}

export interface WizardData {
  general: WizardGeneral;
  theme: WizardTheme;
  pagesMenu: WizardPagesMenu;
  contact: WizardContact;
  products: WizardProducts;
  paymentShipping: WizardPaymentShipping;
  legal: WizardLegal;
  /** Kullanıcının atlamayı seçtiği adımlar. */
  skipped: Record<WizardStepId, boolean>;
}

export const FONT_FAMILY_OPTIONS = [
  'Inter',
  'Poppins',
  'Roboto',
  'Montserrat',
  'Open Sans',
  'Nunito',
  'Lato',
];

export const HEADER_TEMPLATE_OPTIONS: Array<{ id: HeaderTemplateId; label: string; hint: string }> = [
  { id: 'header1', label: 'Header 1', hint: 'Klasik: logo solda, arama ortada' },
  { id: 'header2', label: 'Header 2', hint: 'Modern: geniş arama çubuğu' },
  { id: 'header3', label: 'Header 3', hint: 'Merkezi logo, sade menü' },
  { id: 'header4', label: 'Header 4', hint: 'Kampanya kartlı üst alan' },
  { id: 'header5', label: 'Header 5', hint: 'Minimal, ince çizgili' },
  { id: 'header6', label: 'Header 6', hint: 'Mega menü destekli' },
  { id: 'header7', label: 'Header 7', hint: 'Kurumsal / B2B görünüm' },
];

export const SHOWCASE_KIND_OPTIONS: Array<{ id: ShowcaseAreaKind; label: string }> = [
  { id: 'Bestseller', label: 'Çok Satanlar' },
  { id: 'Trending', label: 'Öne Çıkanlar' },
  { id: 'NewArrival', label: 'Yeni Gelenler' },
  { id: 'Outlet', label: 'Outlet / İndirim' },
];

export function createDefaultWizardData(): WizardData {
  return {
    general: {
      siteName: '',
      faviconUrl: '',
      primaryColor: '#2563eb',
      secondaryColor: '#1d4ed8',
      fontFamily: 'Inter',
      metaTitle: '',
      metaDescription: '',
    },
    theme: {
      headerType: 'header1',
      layoutType: 'modern',
      containerMaxWidth: 1440,
    },
    pagesMenu: {
      menuEnabled: true,
      showHomePage: true,
      showAllCategories: true,
      faqEnabled: true,
      supportEnabled: true,
    },
    contact: {
      address: '',
      phone: '',
      email: '',
      workingHours: '',
      mapEnabled: true,
    },
    products: {
      createShowcase: true,
      showcaseTitle: 'Çok Satanlar',
      showcaseKind: 'Bestseller',
      homepageLimit: 8,
    },
    paymentShipping: {
      shippingEnabled: true,
      freeShippingThreshold: 150,
      shippingCost: 15,
      guestCheckoutEnabled: false,
    },
    legal: {
      seedDefaults: true,
    },
    skipped: {
      general: false,
      theme: false,
      pagesMenu: false,
      contact: false,
      products: false,
      paymentShipping: false,
      legal: false,
    },
  };
}

export const WIZARD_DRAFT_STORAGE_PREFIX = 'ecommerce:site-setup-wizard:';

export function getDraftStorageKey(companyId: string): string {
  return `${WIZARD_DRAFT_STORAGE_PREFIX}${companyId || 'default'}`;
}
